import type { PlayerCardData, RatingSet } from './types'
import { salaryUSD, experienceLabel } from './format'
import {
  ratingColorForValue,
  sectionTint,
  type RatingSection,
} from '../../_shared/ratings'
import { cardTheadRow, cardBodyRow, cardStickyBg } from './cardTableStyles'
import { fmtSimDate } from '@/lib/format'

// Column descriptor for the ratings history table
interface Col {
  key: keyof RatingSet
  label: string
  section: RatingSection
}

const OVR_COL: Col = { key: 'overall', label: 'OVR', section: 'overalls' }

const PITCHER_RATING_COLS: Col[] = [
  { key: 'po', label: 'PO', section: 'pitching' },
  { key: 'fi', label: 'FI', section: 'pitching' },
  { key: 'co', label: 'CO', section: 'pitching' },
  { key: 'sy', label: 'SY', section: 'pitching' },
]

const BATTER_RATING_COLS: Col[] = [
  { key: 'di', label: 'DI', section: 'batting' },
  { key: 'cn', label: 'CN', section: 'batting' },
  { key: 'ba', label: 'BA', section: 'batting' },
  { key: 'sl', label: 'SL', section: 'batting' },
  { key: 'sp', label: 'SP', section: 'batting' },
]

// HR (Hold Runner) is a FIELDING rating and leads the block, but only for
// pitchers: dbo.PlayerOverallFielding weights it into OVRf (simModelWeights.hr
// = 1 at posn 1, 0 everywhere else), while PlayerOverallPitching never sees it.
const HOLD_RUNNER_COL: Col = { key: 'hr', label: 'HR', section: 'fielding' }

const FIELDING_COLS: Col[] = [
  { key: 'ra', label: 'RA', section: 'fielding' },
  { key: 'gl', label: 'GL', section: 'fielding' },
  { key: 'ar', label: 'AR', section: 'fielding' },
]

const EN_COL: Col = { key: 'en', label: 'EN', section: 'endurance' }

function buildHistoryColumns(isPitcher: boolean): Col[] {
  return [
    OVR_COL,
    ...(isPitcher ? PITCHER_RATING_COLS : BATTER_RATING_COLS),
    ...(isPitcher ? [HOLD_RUNNER_COL, ...FIELDING_COLS] : FIELDING_COLS),
    EN_COL,
  ]
}

function RatingsHistoryTable({ player }: { player: PlayerCardData }) {
  // Chronological ascending (youngest age → current); the mapper already sorts
  // by numeric age, so consume as-is. (Do NOT re-sort on the `asOf` string — that
  // mis-ranks yearless "Age N" rows above real year rows.)
  const history = player.ratingsHistory ?? []
  const columns = buildHistoryColumns(player.isPitcher)
  // The position column only earns its place when the position actually moved.
  // Most players never change position, and in a Multi-Position league they
  // cannot going forward - so for them this is a column repeating one value down
  // every row. Driven by the data rather than by the league setting, so a league
  // that ran Standard for years before switching still shows the changes it has.
  const showPosition = new Set(history.map((h) => h.position ?? '')).size > 1
  // Potentials footer — mirrors the full History page: the player's current
  // potentials on one row at the bottom of the ratings table (shown only when
  // potentials exist for this league/player).
  const potAttrs = player.ratings.attrs
  const hasPotentials = potAttrs.overall?.potential != null

  if (history.length === 0) {
    return <p className="text-sm text-slate-500">No ratings history.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-max text-xs tabular-nums">
        <thead>
          <tr className={cardTheadRow}>
            {/* Year — sticky left, needs z-20 so it stays above body cells on scroll */}
            <th className="sticky left-0 z-20 !bg-white px-2 py-1 text-left font-semibold">Year</th>
            <th className="px-2 py-1 text-center font-semibold">Age</th>
            {showPosition && <th className="px-2 py-1 text-center font-semibold">Pos</th>}
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-2 py-1 text-center font-semibold ${sectionTint(col.section, col.key)} ${col.key === 'overall' ? 'border-r-2 border-slate-400' : ''}`}
              >
                {col.label}
              </th>
            ))}
            <th className="px-2 py-1 text-right font-semibold">Salary</th>
          </tr>
        </thead>
        <tbody>
          {history.map((row, i) => (
            // Match the full History page: rows with no season year on record
            // show a blank Year and render italic (the Age column still carries
            // the age), rather than a redundant "Age N" in the Year column.
            <tr key={i} className={`${cardBodyRow} ${row.year == null ? 'italic' : ''}`}>
              {/* Year — sticky left */}
              <td className={`sticky left-0 z-10 ${cardStickyBg(i)} px-2 py-1 text-left font-semibold text-slate-700`}>
                {row.year ?? ''}
              </td>
              <td className="px-2 py-1 text-center text-slate-600">{row.age ?? '—'}</td>
              {showPosition && (
                <td className="px-2 py-1 text-center text-slate-600">{row.position ?? '—'}</td>
              )}
              {columns.map((col) => {
                const r = row.attrs[col.key]
                return (
                  <td key={col.key} className={`px-2 py-1 text-center ${sectionTint(col.section, col.key)} ${col.key === 'overall' ? 'border-r-2 border-slate-400' : ''}`}>
                    <span className={`font-semibold ${ratingColorForValue(r?.current)}`}>
                      {r?.current ?? '—'}
                    </span>
                  </td>
                )
              })}
              <td className="px-2 py-1 text-right text-slate-600">
                {row.salary == null ? '—' : salaryUSD(row.salary)}
              </td>
            </tr>
          ))}
        </tbody>
        {hasPotentials && (
          <tfoot>
            <tr className="border-t-2 border-slate-300">
              {/* Year + Age (+ Pos when it is shown) - must track showPosition or
                  the Potentials row shifts one column out of alignment. */}
              <td className="px-2 py-1 text-left font-semibold text-slate-700" colSpan={showPosition ? 3 : 2}>
                Potentials
              </td>
              {columns.map((col) => {
                const pr = potAttrs[col.key]
                return (
                  <td key={col.key} className={`px-2 py-1 text-center ${sectionTint(col.section, col.key)} ${col.key === 'overall' ? 'border-r-2 border-slate-400' : ''}`}>
                    <span className={`font-semibold ${ratingColorForValue(pr?.potential)}`}>
                      {pr?.potential ?? '—'}
                    </span>
                  </td>
                )
              })}
              <td className="px-2 py-1" />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

export function HistoryTab({ player }: { player: PlayerCardData }) {
  const a = player.acquisition
  return (
    <div className="divide-y divide-slate-100 py-1">
      <section className="py-3 first:pt-1">
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-navy">Career</h3>
        {/* Label/value grid: muted uppercase labels (category) vs darker values
            (data) so the two are easy to tell apart. Drafted detail lives here,
            not on the Summary — the cheat-sheet keeps the one-line bio footer. */}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Debut</dt>
          <dd className="font-medium text-slate-800 tabular-nums">{fmtSimDate(player.bio.debut) || '—'}</dd>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last game</dt>
          <dd className="font-medium text-slate-800 tabular-nums">{fmtSimDate(player.bio.lastGame) || '—'}</dd>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Experience</dt>
          <dd className="font-medium text-slate-800">{experienceLabel(player.bio.experience)}</dd>
          {a.draftRound != null && (
            <>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Drafted</dt>
              <dd className="font-medium text-slate-800 tabular-nums">
                {a.draftYear}, Round {a.draftRound}, #{a.draftPick}{a.draftTeam ? ` by ${a.draftTeam}` : ''}
              </dd>
            </>
          )}
        </dl>
      </section>
      <section className="py-3">
        <h3 className="mb-1.5 text-sm font-bold uppercase tracking-wide text-navy">Transactions</h3>
        {player.transactions && player.transactions.length > 0 ? (
          <ul className="space-y-0.5 text-sm text-slate-700">
            {player.transactions.map((t, i) => (
              <li key={i}>{t.season ? <span className="mr-1 font-semibold text-slate-500 tabular-nums">{t.season}</span> : null}{t.label}</li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-500">None recorded.</p>}
      </section>
      <section className="py-3">
        <h3 className="mb-1.5 text-sm font-bold uppercase tracking-wide text-navy">Ratings &amp; Salary History</h3>
        <RatingsHistoryTable player={player} />
      </section>
      <section className="py-3">
        <h3 className="mb-1.5 text-sm font-bold uppercase tracking-wide text-navy">Achievements</h3>
        {player.achievements && player.achievements.length > 0 ? (
          <ul className="space-y-0.5 text-sm text-slate-700">
            {player.achievements.map((a, i) => (
              <li key={i}>{a.season ? <span className="mr-1 font-semibold text-slate-500 tabular-nums">{a.season}</span> : null}{a.label}</li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-500">None yet.</p>}
      </section>
      <section className="py-3 last:pb-1">
        <h3 className="mb-1.5 text-sm font-bold uppercase tracking-wide text-navy">Awards</h3>
        {player.awards && player.awards.length > 0 ? (
          <ul className="space-y-0.5 text-sm text-slate-700">
            {player.awards.map((a, i) => (
              <li key={i}>{a.season ? <span className="mr-1 font-semibold text-slate-500 tabular-nums">{a.season}</span> : null}{a.label}</li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-500">None yet.</p>}
      </section>
    </div>
  )
}
